import Keycloak from "keycloak-js";
import axios from 'axios';

const _kc = new Keycloak('/keycloak.json');
let isRefreshing = false;
let requests = []
const authCookieName = process.env.REACT_APP_AUTH_COOKIE_NAME?? 'sprw_one_auth';
const authCookieDomain = process.env.REACT_APP_AUTH_COOKIE_DOMAIN?? '.sproboticworks.com';
const commonAppUrl = process.env.REACT_APP_COMMON_URL?? 'https://sproboticworks.com';

/**
 * Initializes Keycloak instance and calls the provided callback function if successfully authenticated.
 *
 * @param onAuthenticatedCallback
 */
const initKeycloak = (onAuthenticatedCallback) => {
    const currentPath = window.location.pathname;
    if(currentPath === '/logout') {
        window.location.href = commonAppUrl+'/logout?client_app=erp_app';
        return;
    }
    _kc.init({
            onLoad: 'check-sso',
            silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
            pkceMethod: 'S256', 
        })
        .then((authenticated) => {
            if (authenticated) {
                onAuthenticatedCallback();
            } else {
                doLogin();
            }
        })
};

const doLogin = _kc.login;

const doLogout = () => {
    clearAuthCookie(authCookieName, authCookieDomain);
    _kc.logout();
};

const getToken = () => _kc.token;

const isLoggedIn = () => !!_kc.token;

const updateToken = (successCallback) =>
    _kc.updateToken(5)
        .then(successCallback)
        .catch(doLogin);

const getUsername = () => _kc.tokenParsed?.preferred_username;

const hasRole = (roles) => roles.some((role) => _kc.hasRealmRole(role));

function initialiseAuthInterceptors() {
    console.log("Initialising auth interceptors");
    axios.interceptors.request.use(config => {
        console.log("Intercepting Request :"+config.url);
        // Do something before request is sent
        config.withCredentials = true;
        let originalRequest = config;
        // let accessTokenExpired = isAccessTokenExpired();
        // let refreshTokenExpired = isRefreshTokenExpired();
        // console.log(originalRequest);
        //console.log("Access Token Expired: "+accessTokenExpired);
        //console.log("Refresh Token Expired: "+refreshTokenExpired);
        if(!originalRequest.url.endsWith('auth/token') && isAccessTokenExpired() && !isRefreshTokenExpired()) {
            console.log("Access Token Expired. Need to generate access token before calling the endpoint:"+originalRequest.url);
            return generateAccessTokenAndCallOriginalRequest(originalRequest);                  
        }
        if(isRefreshTokenExpired()) {
            console.log("Refresh Token Expired. User has to login to access protected routes");
        }
        return config;
    },
    error => {
        console.log(error)
        Promise.reject(error)
    });
    
    axios.interceptors.response.use(res => {
        //console.log(res)
        return res;
    }, 
    error => { 
        console.log(error)
        const originalRequest = error.config;
        if (!originalRequest.url.endsWith('auth/token') 
                && error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            // console.log("Unauthorized error while fetching request. Access Token might be expired.. Refreshing access token!!")
            if(!isRefreshTokenExpired()) {
                return generateAccessTokenAndCallOriginalRequest(originalRequest);
            } else {
                // Do nothing
                // console.log("Refresh Token expired!. Access Token cannot be refreshed!");
            }
        }
        // return Error object with Promise
        return Promise.reject(error);
    });
}


function generateAccessTokenAndCallOriginalRequest(originalRequest) {
    if(!isRefreshing){
        isRefreshing = true;
        return axios.post('/auth/token').then(res => {
            console.log('Access Token refreshed. Call remaining api calls');
            requests.forEach(cb => cb())
            requests = []
            return originalRequest;
        }).catch(res => {
            console.error('Unable to refresh token', res);
        }).finally(() => {
            isRefreshing = false
        })
    }
    else{
        console.log("Someone already called to refresh token.. let me wait!!")
        //store API calls while generating the accesstoken 
        return new Promise((resolve) => {
            requests.push(() => {
                resolve(originalRequest)
            })
        })
    }
}

function isAccessTokenExpired() {
    var authToken = getAuthToken();
    // console.log(authToken);
    return authToken? isJwtExpired(authToken.access_token) : true;
}

function isRefreshTokenExpired() {
    var authToken = getAuthToken();
    return authToken? isJwtExpired(authToken.refresh_token) : true;
}

function getAuthToken() {
    var authCookieValue = getAuthCookie();
    return authCookieValue? JSON.parse(atob(authCookieValue)) : null;
}

function isJwtExpired($token) {
    var expired = false;
    const decoded = parseJwt($token);
    const currentTime = new Date().getTime() / 1000;
    if (currentTime > decoded.exp) 
        expired = true;
    // console.log("Token Expired");
    return expired;
}

function getAuthCookie() {
    let cookie = {};
    document.cookie.split(';').forEach(function(el) {
        let [k,v] = el.split('=');
        cookie[k.trim()] = v;
    })
    let cookieName = authCookieName?? 'sprw_one_auth';
    if(cookie[cookieName]){
        return decodeURIComponent(cookie[cookieName]);
    }
    return null
}

// Set a Cookie
function clearAuthCookie(cName, domain) {
    // let date = new Date();
    // date.setTime(date.getTime() + (expDays * 24 * 60 * 60 * 1000));
    // const expires = "expires=" + date.toUTCString();
    const expires = "expires=Thu, 01 Jan 1970 00:00:01 GMT";
    document.cookie = cName + "=; " + expires + "; path=/; domain=" + domain + "; Secure";
}

function parseJwt (token) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
};

const Auth = {
    initKeycloak,
    doLogin,
    doLogout,
    isLoggedIn,
    getToken,
    updateToken,
    getUsername,
    hasRole,
    initialiseAuthInterceptors,
};

export default Auth;